import { Client } from '../../context/ClientContext';
import { Badge, Button, Icon } from '../ui';
import { getAttention } from './clientsData';

/**
 * Small presentational cells shared by the Clients table and panels.
 */

export function TagChips({ tags, max = 2 }: { tags?: string[]; max?: number }) {
  if (!tags || tags.length === 0) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
  const shown = tags.slice(0, max);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      {shown.map((tag) => (
        <Badge key={tag} tone="neutral">{tag}</Badge>
      ))}
      {tags.length > max ? (
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          +{tags.length - max}
        </span>
      ) : null}
    </span>
  );
}

/** "1/3" task meter with a tiny segmented bar; colored by attention tone. */
export function TaskMeter({ client }: { client: Client }) {
  const pending = client.tasksDueToday;
  const review = client.tasksAwaitingReview;
  const total = pending + review;
  if (total === 0) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
  const att = getAttention(client);
  const toneVar = att ? `var(--status-${att.tone})` : 'var(--status-idle)';
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
      title={`${review} of ${total} done today`}
    >
      <span className="t-num" style={{ fontSize: 'var(--text-sm)' }}>{review}/{total}</span>
      <span style={{ display: 'inline-flex', gap: '2px' }}>
        {Array.from({ length: Math.min(total, 12) }).map((_, i) => (
          <span
            key={i}
            style={{
              width: '10px',
              height: '4px',
              borderRadius: '2px',
              background: i < review ? toneVar : 'var(--surface-active)',
              transition: 'background var(--dur-base) var(--ease-out)',
            }}
          />
        ))}
      </span>
    </span>
  );
}

export function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div style={{ padding: '64px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
      <Icon name="funnel" size={28} style={{ color: 'var(--text-faint)' }} />
      <p style={{ margin: '14px 0 16px', fontSize: 'var(--text-base)' }}>No clients match your filters</p>
      <Button variant="secondary" size="sm" onClick={onClear}>Clear Filters</Button>
    </div>
  );
}
